import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import ToggleButtonGroup from "@mui/joy/ToggleButtonGroup";
import Button from "@mui/joy/Button";
import { BarChart } from "@mui/x-charts/BarChart";
import { AnalyticsFilters, AnalyticsFiltersState } from "../components/AnalyticsFilters";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    WatchSignals,
} from "../components/AnalyticsShared";
import { useAsyncBlock } from "../hooks";
import { bidsTimeStatisticApi } from "../../bidsApi";
import { BidTimeStatistic } from "../../../../models/bidAnalytics";
import { formatInt, formatMoneyKopecks, toNum } from "../format";
import { rollupSeries } from "../rollup";
import { Game } from "../../../../models/games";
import { AnalyticsSeriesChart } from "../components/AnalyticsSeriesChart";

type Mode = "count" | "sum";

type Props = {
    isActive: boolean;
    filters: AnalyticsFiltersState;
    onFiltersChange: (next: AnalyticsFiltersState) => void;
    games: Game[];
    gamesLoading: boolean;
};

export function GamesSplitTab(props: Props) {
    const { isActive, filters, onFiltersChange, games, gamesLoading } = props;
    const [mode, setMode] = React.useState<Mode>("count");

    const [state, refresh] = useAsyncBlock<BidTimeStatistic[]>(
        () =>
            bidsTimeStatisticApi({
                from: filters.from,
                to: filters.to,
                gameId: filters.gameId,
            }),
        isActive,
        [filters.from, filters.to, filters.gameId]
    );

    const aggregated = React.useMemo(() => {
        if (!state.data?.length) {
            return null;
        }
        const acc = {
            csCount: 0,
            csSum: 0,
            csBought: 0,
            csBoughtSum: 0,
            dotaCount: 0,
            dotaSum: 0,
            dotaBought: 0,
            dotaBoughtSum: 0,
            tf2Count: 0,
            tf2Sum: 0,
            tf2Bought: 0,
            tf2BoughtSum: 0,
            rustCount: 0,
            rustSum: 0,
            rustBought: 0,
            rustBoughtSum: 0,
            giftCount: 0,
            giftSum: 0,
            giftBought: 0,
            giftBoughtSum: 0,
            totalCount: 0,
            totalSum: 0,
            totalBought: 0,
            totalBoughtSum: 0,
        };
        for (const row of state.data) {
            acc.csCount += row.csCount;
            acc.csSum += toNum(row.csSum);
            acc.csBought += row.csBought;
            acc.csBoughtSum += toNum(row.csBoughtSum);
            acc.dotaCount += row.dota2Count;
            acc.dotaSum += toNum(row.dota2Sum);
            acc.dotaBought += row.dota2Bought;
            acc.dotaBoughtSum += toNum(row.dota2BoughtSum);
            acc.tf2Count += row.tf2Count;
            acc.tf2Sum += toNum(row.tf2Sum);
            acc.tf2Bought += row.tf2Bought;
            acc.tf2BoughtSum += toNum(row.tf2BoughtSum);
            acc.rustCount += row.rustCount;
            acc.rustSum += toNum(row.rustSum);
            acc.rustBought += row.rustBought;
            acc.rustBoughtSum += toNum(row.rustBoughtSum);
            acc.giftCount += row.giftCount;
            acc.giftSum += toNum(row.giftSum);
            acc.giftBought += row.giftBought;
            acc.giftBoughtSum += toNum(row.giftBoughtSum);
            acc.totalCount += row.totalCount;
            acc.totalSum += toNum(row.totalSum);
            acc.totalBought += row.totalBought;
            acc.totalBoughtSum += toNum(row.totalBoughtSum);
        }
        return acc;
    }, [state.data]);

    const chartData = React.useMemo(() => {
        if (!state.data?.length) {
            return [];
        }
        const rolled = rollupSeries(state.data, filters.granularity, {
            sumKeys: [
                "csCount",
                "csSum",
                "csBought",
                "csBoughtSum",
                "dota2Count",
                "dota2Sum",
                "tf2Count",
                "tf2Sum",
                "rustCount",
                "rustSum",
                "giftCount",
                "giftSum",
            ],
        });
        return rolled.map((row) => ({
            ...row,
            cs: mode === "count" ? row.csCount : toNum(row.csSum) / 100,
            dota: mode === "count" ? row.dota2Count : toNum(row.dota2Sum) / 100,
            tf2: mode === "count" ? row.tf2Count : toNum(row.tf2Sum) / 100,
            rust: mode === "count" ? row.rustCount : toNum(row.rustSum) / 100,
            gift: mode === "count" ? row.giftCount : toNum(row.giftSum) / 100,
        }));
    }, [state.data, filters.granularity, mode]);

    const remainingValues = aggregated
        ? mode === "count"
            ? [aggregated.csCount, aggregated.dotaCount, aggregated.tf2Count, aggregated.rustCount, aggregated.giftCount]
            : [
                  aggregated.csSum / 100,
                  aggregated.dotaSum / 100,
                  aggregated.tf2Sum / 100,
                  aggregated.rustSum / 100,
                  aggregated.giftSum / 100,
              ]
        : [];
    const boughtValues = aggregated
        ? mode === "count"
            ? [
                  aggregated.csBought,
                  aggregated.dotaBought,
                  aggregated.tf2Bought,
                  aggregated.rustBought,
                  aggregated.giftBought,
              ]
            : [
                  aggregated.csBoughtSum / 100,
                  aggregated.dotaBoughtSum / 100,
                  aggregated.tf2BoughtSum / 100,
                  aggregated.rustBoughtSum / 100,
                  aggregated.giftBoughtSum / 100,
              ]
        : [];

    const valueFmt = (v: number | null) => {
        if (v == null) {
            return "—";
        }
        return mode === "count" ? formatInt(v) : `${v.toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`;
    };

    const isEmpty = aggregated != null && aggregated.totalCount === 0 && aggregated.totalBought === 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <AnalyticsFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showPeriod
                showGranularity
            />

            <AnalyticsCard
                title="Спрос по играм"
                subtitle="Остаток и купленные позиции в разрезе игр. Купленное включает недоисполненные заявки."
                snapshotLabel="period"
                howToRead={
                    <Typography level="body-sm">
                        Доли по играм — остаток свободных позиций или их сумма в ₽. Ряд «Куплено» на итоговом
                        баре — выкуп, в том числе из ещё открытых заявок.
                    </Typography>
                }
                watchFor={
                    <WatchSignals
                        items={[
                            {
                                signal: "Перекос спроса в одну игру",
                                growsIsGood: false,
                                ifGrows: "внезапный перекос без причины — возможна аномалия, акция конкурентов или сбой фильтров/игр.",
                                ifFalls: "спрос распределяется равномернее между играми.",
                            },
                            {
                                signal: "Доля основной («кормящей») игры",
                                ifGrows: "ожидаемый рост после ивента, обновления или нашей акции.",
                                ifFalls: "основной источник спроса слабеет — проверьте офферы и цены именно в этой игре.",
                            },
                        ]}
                    />
                }
            >
                {state.loading && <BlockLoading />}
                {!state.loading && state.error && <BlockError message={state.error} onRetry={refresh} />}
                {!state.loading && !state.error && isEmpty && <EmptyState />}
                {!state.loading && !state.error && aggregated && !isEmpty && (
                    <>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            <MetricHint label="Остаток шт." value={formatInt(aggregated.totalCount)} />
                            <MetricHint
                                label="Куплено шт."
                                value={formatInt(aggregated.totalBought)}
                                color="success"
                            />
                            <MetricHint
                                label="Сумма остатка"
                                value={formatMoneyKopecks(aggregated.totalSum)}
                            />
                        </Box>

                        <ToggleButtonGroup
                            size="sm"
                            value={mode}
                            onChange={(_, v) => v && setMode(v as Mode)}
                            sx={{ alignSelf: "flex-start" }}
                        >
                            <Button value="count">Количество</Button>
                            <Button value="sum">Сумма ₽</Button>
                        </ToggleButtonGroup>

                        <Typography level="title-sm">Динамика</Typography>
                        <AnalyticsSeriesChart
                            dataset={chartData}
                            granularity={filters.granularity}
                            series={[
                                { dataKey: "cs", label: "CS2", color: "#f57c00", valueFormatter: valueFmt },
                                { dataKey: "dota", label: "Dota 2", color: "#c62828", valueFormatter: valueFmt },
                                { dataKey: "tf2", label: "TF2", color: "#6a1b9a", valueFormatter: valueFmt },
                                { dataKey: "rust", label: "Rust", color: "#8884d8", valueFormatter: valueFmt },
                                { dataKey: "gift", label: "Gifts", color: "#2e7d32", valueFormatter: valueFmt },
                            ]}
                        />

                        <Typography level="title-sm">Итого за период</Typography>
                        <BarChart
                            height={280}
                            series={[
                                {
                                    data: remainingValues,
                                    label: mode === "count" ? "Остаток" : "Остаток ₽",
                                    color: "#1976d2",
                                    valueFormatter: (v) =>
                                        mode === "count"
                                            ? formatInt(v ?? 0)
                                            : `${(v ?? 0).toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`,
                                },
                                {
                                    data: boughtValues,
                                    label: mode === "count" ? "Куплено" : "Куплено ₽",
                                    color: "#2e7d32",
                                    valueFormatter: (v) =>
                                        mode === "count"
                                            ? formatInt(v ?? 0)
                                            : `${(v ?? 0).toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`,
                                },
                            ]}
                            xAxis={[
                                {
                                    data: ["CS2", "Dota 2", "TF2", "Rust", "Gifts"],
                                    scaleType: "band",
                                },
                            ]}
                            margin={{ left: 60, right: 20, top: 20, bottom: 40 }}
                        />
                    </>
                )}
            </AnalyticsCard>
        </Box>
    );
}
